<script setup lang="ts">
import { FormKitLazyProvider } from '@formkit/vue'
import type { Server, ServerForm } from '~/types/servers'
import { DEFAULT_PERMISSIONS_BY_ROLE } from '~/types/staffPermissions'

type SanitizedServer = Omit<Server, 'pin_code'>

const { t } = useI18n()
const dialogRef = inject('dialogRef') as { value?: { data?: { server?: SanitizedServer }, close?: () => void } } | undefined
const server = computed(() => dialogRef?.value?.data?.server)
const serverId = computed(() => server.value?.id)
const serversStore = useServersStore()

const formModel = ref({
  name: server.value?.name ?? '',
  employee_number: server.value?.employee_number || '',
  role: server.value?.role ?? 'staff',
  active: server.value?.active ?? true,
  pin_code: '',
  pin_confirm: '',
})

const permissions = ref(server.value?.permissions || DEFAULT_PERMISSIONS_BY_ROLE(server.value?.role ?? 'staff'))
const showResetPin = ref(false)

watch(() => formModel.value.role, (newRole) => {
  permissions.value = DEFAULT_PERMISSIONS_BY_ROLE(newRole)
})

async function handleSubmit(): Promise<void> {
  if (!serverId.value) return

  const payload: Partial<ServerForm> = {
    name: formModel.value.name,
    employee_number: formModel.value.employee_number,
    role: formModel.value.role,
    active: formModel.value.active,
    permissions: permissions.value,
  }

  if (showResetPin.value && formModel.value.pin_code) {
    payload.pin_code = formModel.value.pin_code
  }

  const success = await serversStore.updateServer(serverId.value, payload)
  if (success) {
    dialogRef?.value?.close?.()
  }
}

function toggleResetPin() {
  showResetPin.value = !showResetPin.value
  if (!showResetPin.value) {
    formModel.value.pin_code = ''
    formModel.value.pin_confirm = ''
  }
}
</script>

<template>
  
<FormKitLazyProvider config-file="true">
<FormKit
    id="server-edit-form"
    v-model="formModel"
    class="flex"
    type="form"
    :actions="false"
    @submit="handleSubmit"
  >
    <div class="flex gap-3 justify-center">
      <div class="w-1/2">
        <FormKit
          type="text"
          :label="t('Name')"
          :placeholder="t('Maria Garcia')"
          validation="required"
          validation-visibility="live"
          name="name"
        />
        <FormKit
          type="select"
          :label="t('Role')"
          :options="[
            { label: t('Staff'), value: 'staff' },
            { label: t('Manager'), value: 'manager' },
            { label: t('Admin'), value: 'admin' },
          ]"
          validation="required"
          name="role"
        />
        <FormKit
          type="text"
          :label="t('Employee Number')"
          :placeholder="t('Optional')"
          name="employee_number"
        />
        <FormKit
          type="checkbox"
          :label="t('Active')"
          name="active"
        />

        <div class="my-3">
          <Button
            type="button"
            text
            size="small"
            :label="showResetPin ? t('Cancel') : t('Reset PIN')"
            :icon="showResetPin ? 'pi pi-times' : 'pi pi-key'"
            @click="toggleResetPin"
          />
        </div>

        <template v-if="showResetPin">
          <FormKit
            type="text"
            :label="t('PIN Code')"
            :placeholder="t('4-6 digit PIN')"
            validation="required|number|length:4,6"
            :validation-messages="{
              number: t('PIN must contain only digits'),
              length: t('PIN must be 4-6 digits'),
            }"
            validation-visibility="live"
            name="pin_code"
            inputmode="numeric"
          />
          <FormKit
            type="text"
            :label="t('Confirm PIN')"
            :placeholder="t('4-6 digit PIN')"
            validation="required|confirm:pin_code"
            validation-visibility="live"
            name="pin_confirm"
            inputmode="numeric"
          />
        </template>

        <h3 class="text-sm font-semibold text-gray-700 mt-4 mb-3">
          {{ t('Permissions') }}
        </h3>
        <AdminServersStaffPermissionsForm v-model="permissions" />

        <FormKit
          type="submit"
          :label="t('Update server')"
        />
      </div>
    </div>
  </FormKit>
</FormKitLazyProvider>

</template>
